import { ref, get, set, onValue } from 'firebase/database';
import { database } from '../firebase';
import { encodeIP, decodeIP } from '../utils/ipUtils';

export class UserAdapter {
  static async initializeUser(ip, country) {
    const encodedIP = encodeIP(ip);
    const userRef = ref(database, `users/${encodedIP}`);
    const snapshot = await get(userRef);
    
    if (!snapshot.exists()) {
      // New user
      await set(userRef, {
        score: 0,
        country,
        lastActive: Date.now(),
        prayCount: 0,
        punchCount: 0,
        created: Date.now(),
        ip: ip // Store original IP in the data
      });
      return { score: 0, prayCount: 0, punchCount: 0 };
    }
    
    // Existing user - update last active
    const userData = snapshot.val();
    await set(userRef, {
      ...userData,
      lastActive: Date.now()
    });
    
    return userData;
  }

  static async updateUserScore(ip, action) {
    const encodedIP = encodeIP(ip);
    const userRef = ref(database, `users/${encodedIP}`);
    const snapshot = await get(userRef);
    
    if (!snapshot.exists()) return;
    
    const userData = snapshot.val();
    const scoreChange = action === 'PRAY' ? 1 : -1;
    
    await set(userRef, {
      ...userData,
      score: userData.score + scoreChange,
      lastActive: Date.now(),
      [action.toLowerCase() + 'Count']: userData[action.toLowerCase() + 'Count'] + 1
    });
    
    return userData.score + scoreChange;
  }

  static subscribeToUser(ip, callback) {
    const encodedIP = encodeIP(ip);
    const userRef = ref(database, `users/${encodedIP}`);
    return onValue(userRef, (snapshot) => {
      if (snapshot.exists()) {
        callback(snapshot.val());
      }
    });
  }

  static async getUserStats(ip) {
    const encodedIP = encodeIP(ip);
    const userRef = ref(database, `users/${encodedIP}`);
    const snapshot = await get(userRef);
    
    if (!snapshot.exists()) return null;
    
    const userData = snapshot.val();
    return {
      score: userData.score,
      prayCount: userData.prayCount,
      punchCount: userData.punchCount,
      ratio: userData.punchCount === 0 ? 
        userData.prayCount : 
        userData.prayCount / userData.punchCount
    };
  }

  static async getUserContext() {
    try {
      // Get the user's IP and country from window globals
      const ip = window.userIP;
      const country = window.userCountry || 'unknown';
      const encodedIP = encodeIP(ip);

      // Get user's stats from Firebase
      const userRef = ref(database, `users/${encodedIP}`);
      const snapshot = await get(userRef);
      const userStats = snapshot.exists() ? snapshot.val() : {
        score: 0,
        prayCount: 0,
        punchCount: 0,
        lastActive: Date.now()
      };

      return {
        ip,
        encodedIP,
        country,
        score: userStats.score || 0,
        prayCount: userStats.prayCount || 0,
        punchCount: userStats.punchCount || 0,
        lastActive: userStats.lastActive || Date.now()
      };
    } catch (error) {
      console.error('Error getting user context:', error);
      // Return default context if there's an error
      return {
        ip: window.userIP,
        encodedIP: encodeIP(window.userIP),
        country: window.userCountry || 'unknown',
        score: 0,
        prayCount: 0,
        punchCount: 0,
        lastActive: Date.now()
      };
    }
  }

  static async updateUserScore(action) {
    try {
      const ip = window.userIP;
      const encodedIP = encodeIP(ip);
      const userRef = ref(database, `users/${encodedIP}`);
      
      // Get current user data
      const snapshot = await get(userRef);
      const userData = snapshot.exists() ? snapshot.val() : {
        score: 0,
        prayCount: 0,
        punchCount: 0,
        lastActive: Date.now()
      };

      // Update counts and score
      const scoreChange = action === 'PRAY' ? 1 : -1;
      
      // Update the specific count based on action
      const updates = {
        score: (userData.score || 0) + scoreChange,
        lastActive: Date.now()
      };
      
      if (action === 'PRAY') {
        updates.prayCount = (userData.prayCount || 0) + 1;
      } else {
        updates.punchCount = (userData.punchCount || 0) + 1;
      }

      // Save updates
      await set(userRef, {
        ...userData,
        ...updates
      });

      return updates;
    } catch (error) {
      console.error('Error updating user score:', error);
      throw error;
    }
  }
} 